/**
 * Converting between a stored ISO date and the three parts a person types.
 *
 * Separate from `DateField` because it is pure: what counts as a date is
 * testable without rendering, and a component file that also exports functions
 * breaks fast refresh.
 */

export interface DateParts {
  readonly day: string;
  readonly month: string;
  readonly year: string;
}

/** Splits an ISO date for editing. Anything unrecognised edits as empty. */
export function toParts(iso: string): DateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return { day: '', month: '', year: '' };

  return { year: match[1] ?? '', month: match[2] ?? '', day: match[3] ?? '' };
}

/**
 * Rebuilds an ISO date, or an empty string while it is still incomplete.
 *
 * A half-typed date is not a wrong date — returning something invalid here
 * would make the form shout at somebody who is still typing.
 */
export function toIso({ day, month, year }: DateParts): string {
  if (day === '' || month === '' || year === '') return '';
  // Four digits, so a typed "08" is never quietly read as 2008 or 1908.
  if (year.length !== 4) return '';

  const dayNumber = Number(day);
  const monthNumber = Number(month);

  if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > 31) return '';
  if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) return '';
  if (!Number.isInteger(Number(year))) return '';

  const iso = [
    year,
    String(monthNumber).padStart(2, '0'),
    String(dayNumber).padStart(2, '0'),
  ].join('-');

  // Rejects 31/02, which the ranges above accept.
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';

  return parsed.toISOString().slice(0, 10) === iso ? iso : '';
}
